import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { RevealOnView } from "@/components/Reveal";
import { AXIOM } from "@/constants/testIds";

const INDUSTRIES = [
  {
    key: "mobility",
    title: "Mobility & EV",
    items: ["Powertrain integration", "Battery pack structure", "Thermal management", "NVH & durability"],
    img: "https://images.unsplash.com/photo-1550355291-bbee04a92027?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    key: "aerospace",
    title: "Aerospace & Defence",
    items: ["External aerodynamics", "Structural FEA", "Fatigue & fracture", "Reliability at margin"],
    img: "https://images.unsplash.com/photo-1517976487492-5750f3195933?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    key: "consumer",
    title: "Consumer Products",
    items: ["Class-A surface modelling", "DFM & tolerance stack-ups", "Pilot manufacturing", "Colour, material & finish"],
    img: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    key: "industrial",
    title: "Industrial Equipment",
    items: ["Heavy-duty CNC", "Welded frames & weldments", "Precision assemblies", "Hydraulic & pneumatic systems"],
    img: "https://images.unsplash.com/photo-1518623489648-a173ef7824f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    key: "medical",
    title: "Medical Devices",
    items: ["Compliance-ready design", "Design controls & DHF", "Injection-mold tooling", "Reliability & shelf-life"],
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    key: "robotics",
    title: "Robotics & Automation",
    items: ["Actuator & drivetrain", "Structural design", "Sensor & control integration", "Endurance testing"],
    img: "https://images.unsplash.com/photo-1535378917042-10a22c95931a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
];

export default function Industries() {
  const [openKey, setOpenKey] = useState("aerospace");
  const active = INDUSTRIES.find((i) => i.key === openKey) || INDUSTRIES[0];

  return (
    <section id="industries" data-testid={AXIOM.industries} className="bg-bg py-24 md:py-32 border-t border-rule">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <RevealOnView>
          <div className="eyebrow mb-5">Industries served</div>
          <h2
            className="font-display text-ink max-w-[18ch]"
            style={{ fontSize: "clamp(30px, 4vw, 46px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}
          >
            Built for the systems <br className="hidden md:block" /> you actually ship.
          </h2>
          <p className="text-ink/70 mt-5 max-w-[54ch]" style={{ fontSize: "16px", lineHeight: 1.6 }}>
            From external aerodynamics to precision manufacturing, we run the
            engineering that product teams depend on - across industries that
            live or die by physics.
          </p>
        </RevealOnView>

        <div className="mt-12 grid grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left column - accordion list */}
          <div className="col-span-12 md:col-span-6 border-t border-rule">
            {INDUSTRIES.map((ind) => {
              const isOpen = ind.key === openKey;
              return (
                <div
                  key={ind.key}
                  data-testid={`industry-item-${ind.key}`}
                  className="border-b border-rule"
                >
                  <button
                    onClick={() => setOpenKey(isOpen ? null : ind.key)}
                    className="w-full flex items-center justify-between text-left py-6 md:py-7 group"
                    aria-expanded={isOpen}
                  >
                    <span
                      className={`font-display transition-colors ${
                        isOpen ? "text-forge" : "text-ink group-hover:text-forge"
                      }`}
                      style={{ fontSize: "clamp(20px, 2.2vw, 26px)", letterSpacing: "-0.02em" }}
                    >
                      {ind.title}
                    </span>
                    <span className={`transition-colors ${isOpen ? "text-forge" : "text-smoke"}`}>
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <ul className="pb-6 md:pb-7 space-y-3">
                          {ind.items.map((it) => (
                            <li
                              key={it}
                              className="flex items-center gap-3 text-ink/85"
                              style={{ fontSize: "15px" }}
                            >
                              <ArrowRight className="w-3.5 h-3.5 text-forge shrink-0" />
                              {it}
                            </li>
                          ))}
                        </ul>
                        <a
                          href="#contact"
                          className="inline-flex items-center gap-2 pb-6 md:pb-7 font-mono text-[11px] tracking-[0.14em] uppercase text-ink hover:text-forge transition-colors group"
                        >
                          Explore {ind.title.split(" ")[0].toLowerCase()}
                          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                        </a>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Right column - active industry image */}
          <div className="col-span-12 md:col-span-6 md:sticky md:top-24">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.key}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full aspect-[4/3] overflow-hidden border border-rule bg-panel"
              >
                <img
                  src={active.img}
                  alt={active.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-ink/25 via-transparent to-transparent" />
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <span className="w-2 h-2 bg-forge inline-block" />
                  <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-white">
                    Fig. {active.title}
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
